import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import { getVcompNotifications } from "../dashboardSlice";
import VcompParticipateNotification from "../../../components/dashboard/Vcomp/VcompParticipateNotification";
import VcompManageNotification from "../../../components/dashboard/Vcomp/VcompManageNotification";

/* ── glassmorphic card base ── */
const glassmorphicCardSx = {
    borderRadius: "16px",
    background:
        "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(245,248,255,0.92) 50%)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(148,163,184,0.28)",
    boxShadow: "0 8px 32px rgba(15, 23, 42, 0.10)",
    overflow: "hidden",
    position: "relative",
};

const accentBarSx = (gradient) => ({
    height: 5,
    background: gradient,
    flexShrink: 0,
});

export default function MobileDashboardVcomp({ compact = false, showEmpty = true }) {
    const dispatch = useDispatch();
    const fetchedRef = useRef(false);
    const canViewVcomp = checkPermission(
        config.scopes.vcomp,
        config.permissionTypes.view
    );
    const participateProjects = useSelector(
        (state) => state.dashboard.vcompParticipateNotification
    );
    const managedProjects = useSelector(
        (state) => state.dashboard.vcompManageNotification
    );
    const showParticipations =
        Array.isArray(participateProjects) && participateProjects.length > 0;
    const showManagement =
        Array.isArray(managedProjects) && managedProjects.length > 0;

    useEffect(() => {
        if (canViewVcomp && !fetchedRef.current) {
            dispatch(getVcompNotifications());
            fetchedRef.current = true;
        }
    }, [canViewVcomp, dispatch]);

    if (!canViewVcomp) {
        return null;
    }

    const renderParticipationsCard = () => (
        <Card sx={glassmorphicCardSx}>
            <Box
                sx={accentBarSx(
                    "linear-gradient(90deg, #2563eb, rgba(255,255,255,0.85), #2563eb)"
                )}
            />
            <CardContent sx={{ p: 2, position: "relative" }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: "1rem",
                        letterSpacing: "-0.01em",
                        color: "rgba(15, 23, 42, 0.88)",
                    }}
                >
                    Vcomp - Participations
                </Typography>
                <VcompParticipateNotification />
            </CardContent>
        </Card>
    );

    const renderManagementCard = () => (
        <Card sx={glassmorphicCardSx}>
            <Box
                sx={accentBarSx(
                    "linear-gradient(90deg, #FF9015, rgba(255,193,0,0.85), #FF9015)"
                )}
            />
            <CardContent sx={{ p: 2, position: "relative" }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: "1rem",
                        letterSpacing: "-0.01em",
                        color: "rgba(15, 23, 42, 0.88)",
                    }}
                >
                    Vcomp - Management
                </Typography>
                <VcompManageNotification />
            </CardContent>
        </Card>
    );

    const renderEmptyState = () => {
        if (!showEmpty) return null;
        return (
            <Card
                sx={{
                    ...glassmorphicCardSx,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 80,
                    p: 2,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: "rgba(107, 114, 128, 0.8)",
                        fontWeight: 500,
                        textAlign: "center",
                    }}
                >
                    Aucune participation Vcomp disponible.
                </Typography>
            </Card>
        );
    };

    if (compact) {
        if (!showParticipations && !showManagement) {
            return renderEmptyState();
        }
        if (showParticipations) {
            return renderParticipationsCard();
        }
        return renderEmptyState();
    }

    if (!showParticipations && !showManagement) {
        return renderEmptyState();
    }

    return (
        <Stack spacing={2}>
            {showParticipations && renderParticipationsCard()}
            {showManagement && renderManagementCard()}
        </Stack>
    );
}
